import { useNavigate } from "react-router-dom";
import { Footprints } from "lucide-react";
import { StatusBadge } from "./StatusBadge";
import { FavoriteButton } from "./FavoriteButton";
import { Rating } from "./Rating";
import { formatDistance } from "@/lib/format";
import { useLanguage } from "@/i18n/LanguageContext";

export const SpotCard = ({ spot, className = "", imageClassName = "h-48" }) => {
  const navigate = useNavigate();
  const { t, lang } = useLanguage();
  return (
    <article
      data-testid={`spot-card-${spot.slug}`}
      onClick={() => navigate(`/etablissement/${spot.slug}`)}
      className={`group flex h-full cursor-pointer flex-col overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-[0_4px_20px_rgba(15,23,42,0.06)] transition duration-300 hover:-translate-y-1 hover:border-blue-200 hover:shadow-[0_12px_32px_rgba(37,99,235,0.12)] ${className}`}
    >
      <div className={`relative shrink-0 overflow-hidden ${imageClassName}`}>
        <img
          src={spot.image}
          alt={spot.name}
          loading="lazy"
          className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
        />
        <div className="absolute left-3 top-3 flex flex-col items-start gap-1.5">
          <StatusBadge badge={spot.badge} />
          {spot.is_demo && (
            <span data-testid={`demo-badge-${spot.slug}`} className="rounded-full bg-amber-400/95 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-950 shadow-sm">
              {t("Démo")}
            </span>
          )}
        </div>
        <FavoriteButton item={spot} itemType="etablissement" className="absolute right-3 top-3" />
      </div>
      <div className="flex flex-1 flex-col space-y-2 p-5">
        <h3 className="line-clamp-2 min-h-[2.75rem] text-base font-semibold leading-snug text-slate-900">{spot.name}</h3>
        <p className="truncate text-sm text-slate-500">
          {t(spot.category_label)} • {spot.neighborhood}
        </p>
        <div className="flex items-center justify-between gap-2">
          <Rating value={spot.rating} count={spot.reviews_count} />
          <span className="flex shrink-0 items-center gap-1 text-xs text-slate-400">
            <Footprints className="h-3.5 w-3.5" />
            {formatDistance(spot.distance_m, lang)}
          </span>
        </div>
        <div className="mt-auto min-h-[1.375rem]">
          {spot.price_from ? (
            <span data-testid="price-from" className="inline-flex items-center pt-0.5 text-xs font-semibold text-[#2563EB]">
              {lang === "en" ? `From $${spot.price_from}` : `À partir de ${spot.price_from} $`}
            </span>
          ) : (
            spot.is_open && (
              <span data-testid="open-status" className="inline-flex items-center gap-1.5 pt-0.5 text-xs font-semibold text-emerald-600">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                {t("Ouvert")}
              </span>
            )
          )}
        </div>
      </div>
    </article>
  );
};
